'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Play, X } from 'lucide-react';
import { useTrack } from '@owlcat/app-shell';

export interface ProofItem {
  id: string;
  title: string;
  kindLabel: string;
  thumb: string | null;
  youtubeVideoId: string | null;
}

/**
 * The proof strip plays where it stands.
 *
 * Every card carried a play badge and then navigated to the gallery index — a
 * press that promised a story and delivered a list, with the landing (and the
 * one thing the visitor came to decide) left behind. A card now opens the story
 * right here; "see them all" is still a link, because that one IS a list.
 *
 * A story with no video (audio-only, not yet uploaded) keeps the link: there is
 * nothing to play in place.
 */
export function GalleryProofCards({
  items,
  galleryHref,
  closeLabel,
}: {
  items: ProofItem[];
  galleryHref: string;
  closeLabel: string;
}) {
  const [playing, setPlaying] = useState<ProofItem | null>(null);
  const track = useTrack();

  useEffect(() => {
    if (!playing) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPlaying(null);
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [playing]);

  const card = (it: ProofItem) => (
    <>
      {it.thumb ? (
        // eslint-disable-next-line @next/next/no-img-element -- external YouTube/proxy thumb
        <img
          src={it.thumb}
          alt={it.title}
          className="size-full object-cover transition-transform group-hover:scale-105"
          loading="lazy"
        />
      ) : (
        <div className="size-full bg-gradient-to-br from-brand-blue via-brand-purple to-brand-pink" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      <div className="absolute left-2 top-2 flex size-9 items-center justify-center rounded-full bg-white/90 text-primary shadow">
        <Play className="size-4 fill-current" />
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3">
        <p className="line-clamp-1 text-sm font-medium text-white">{it.title}</p>
        <p className="text-[11px] uppercase tracking-wide text-white/70">{it.kindLabel}</p>
      </div>
    </>
  );

  return (
    <>
      <div className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
        {items.map((it) =>
          it.youtubeVideoId ? (
            <button
              key={it.id}
              type="button"
              onClick={() => {
                setPlaying(it);
                track('landing_demo_played', { app_id: 'portal', surface: 'video', language: '' });
              }}
              className="group relative aspect-video w-64 shrink-0 overflow-hidden rounded-xl border bg-muted text-left"
              data-testid={`landing-proof-card-${it.id}`}
            >
              {card(it)}
            </button>
          ) : (
            <Link
              key={it.id}
              href={galleryHref}
              className="group relative aspect-video w-64 shrink-0 overflow-hidden rounded-xl border bg-muted"
              data-testid={`landing-proof-card-${it.id}`}
            >
              {card(it)}
            </Link>
          ),
        )}
      </div>

      {playing?.youtubeVideoId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPlaying(null)}
          data-testid="landing-proof-player"
        >
          <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPlaying(null)}
              aria-label={closeLabel}
              className="absolute -top-10 right-0 flex size-9 items-center justify-center rounded-full bg-white/90 text-foreground shadow"
            >
              <X className="size-4" />
            </button>
            <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
              <iframe
                className="absolute inset-0 size-full"
                src={`https://www.youtube-nocookie.com/embed/${playing.youtubeVideoId}?autoplay=1&rel=0`}
                title={playing.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
